import { useMemo } from "react";
import { Users, Eye, Edit3 } from "lucide-react";
import { useShares } from "../contexts/SharesContext";
import type { ResourceType } from "../services/sharing.service";

interface CollaboratorIndicatorProps {
  resourceType: ResourceType;
  resourceId: number;
}

interface CollaboratorCounts {
  total: number;
  viewers: number;
  editors: number;
}

export function CollaboratorIndicator({ resourceType, resourceId }: CollaboratorIndicatorProps) {
  const { getSharesForResource } = useShares();

  const counts = useMemo((): CollaboratorCounts | null => {
    try {
      const shares = getSharesForResource(resourceType, resourceId);

      // Filtrar apenas colaboradores explícitos aceitos
      const explicitCollaborators = shares.filter(
        (share) => share.status === "accepted" && !share.isImplicitOwner
      );

      const viewers = explicitCollaborators.filter((s) => s.permission === "view").length;
      const editors = explicitCollaborators.filter((s) => s.permission === "edit").length;

      return {
        total: explicitCollaborators.length,
        viewers,
        editors,
      };
    } catch (err) {
      console.error('Erro ao processar shares:', err);
      return null;
    }
  }, [resourceType, resourceId, getSharesForResource]);

  if (!counts || counts.total === 0) {
    return null;
  }

  const tooltipText = [
    counts.viewers > 0 && `${counts.viewers} visualizador${counts.viewers > 1 ? "es" : ""}`,
    counts.editors > 0 && `${counts.editors} editor${counts.editors > 1 ? "es" : ""}`,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="flex items-center gap-1.5 text-xs text-gray-500 cursor-help"
      title={tooltipText}
    >
      <Users size={12} />
      <span>{counts.total}</span>
      {counts.viewers > 0 && (
        <div className="flex items-center gap-0.5 text-blue-500">
          <Eye size={10} />
          <span>{counts.viewers}</span>
        </div>
      )}
      {counts.editors > 0 && (
        <div className="flex items-center gap-0.5 text-green-500">
          <Edit3 size={10} />
          <span>{counts.editors}</span>
        </div>
      )}
    </div>
  );
}

